import { useId } from "react";
import {
  BOARD_STYLES,
  PIECE_SETS,
  SITE_THEMES,
  resetAppearance,
  setAppearance,
  type BoardStyle,
  type PieceSet,
  type SiteTheme,
} from "../../appearance/appearance.ts";
import { useAppearance } from "../../appearance/useAppearance.ts";
import { ModalDialog } from "./ModalDialog.tsx";

function Meter({ label, value }: { label: string; value: number }) {
  return (
    <span
      className="appearance-meter"
      aria-label={`${label}: ${value} of 5`}
      title={`${label}: ${value} of 5`}
    >
      <span className="appearance-meter-label">{label}</span>
      <span className="appearance-meter-dots" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} className={n <= value ? "is-on" : ""} />
        ))}
      </span>
    </span>
  );
}

function BoardPreview({ board }: { board: BoardStyle }) {
  const squares: Array<{ x: number; y: number; dark: boolean }> = [];
  for (let y = 0; y < 4; y += 1) {
    for (let x = 0; x < 6; x += 1) squares.push({ x, y, dark: (x + y) % 2 === 1 });
  }
  return (
    <div data-board={board.id} className="board-preview-wrap">
      <svg
        className="cm-chessboard default board-preview"
        viewBox="0 0 60 40"
        aria-hidden="true"
        focusable="false"
      >
        <g className="board">
          {squares.map((sq) => (
            <rect
              key={`${sq.x}-${sq.y}`}
              className={`square ${sq.dark ? "black" : "white"}`}
              x={sq.x * 10}
              y={sq.y * 10}
              width="10"
              height="10"
            />
          ))}
        </g>
      </svg>
    </div>
  );
}

function ThemePreview({ theme }: { theme: SiteTheme }) {
  return (
    <div data-theme={theme.id} className="theme-preview" aria-hidden="true">
      <div className="theme-preview-bar">
        <span className="theme-preview-logo">15</span>
        <span className="theme-preview-nav" />
        <span className="theme-preview-nav is-primary" />
      </div>
      <div className="theme-preview-body">
        <span className="theme-preview-eyebrow">Now learning</span>
        <span className="theme-preview-title">Italian Game</span>
        <span className="theme-preview-line" />
        <span className="theme-preview-line short" />
        <span className="theme-preview-chips">
          <span className="theme-preview-chip" />
          <span className="theme-preview-chip gold" />
        </span>
      </div>
    </div>
  );
}

function OptionButton({
  selected,
  onSelect,
  name,
  blurb,
  children,
  meta,
}: {
  selected: boolean;
  onSelect: () => void;
  name: string;
  blurb: string;
  children: React.ReactNode;
  meta?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`appearance-option${selected ? " is-selected" : ""}`}
      onClick={onSelect}
    >
      <span className="appearance-option-preview">{children}</span>
      <span className="appearance-option-text">
        <span className="appearance-option-name">
          {name}
          {selected ? <span className="appearance-option-check">Selected</span> : null}
        </span>
        <span className="appearance-option-blurb">{blurb}</span>
        {meta ? <span className="appearance-option-meta">{meta}</span> : null}
      </span>
    </button>
  );
}

export function AppearanceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const appearance = useAppearance();
  const titleId = useId();
  const piecesId = useId();
  const boardsId = useId();
  const themesId = useId();

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      className="help-modal appearance-modal"
      id="appearanceModal"
    >
      <article className="help-inner appearance-inner">
        <header className="help-header">
          <div>
            <p className="eyebrow">Appearance</p>
            <h2 id={titleId}>Pieces, board and site</h2>
            <p className="appearance-sub">
              Every choice is cosmetic and saved on this device. The first option in each row is the
              original look.
            </p>
          </div>
          <button className="help-close" type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="appearance-body" role="region" aria-label="Appearance choices" tabIndex={0}>
          <section className="help-section appearance-section" aria-labelledby={piecesId}>
            <h3 id={piecesId}>Pieces</h3>
            <p className="appearance-hint">
              Sets are rated on two axes. The most readable sets are the plainest; the most
              beautiful ask a little more of your eye.
            </p>
            <div className="appearance-grid" role="radiogroup" aria-labelledby={piecesId}>
              {PIECE_SETS.map((set: PieceSet) => (
                <OptionButton
                  key={set.id}
                  selected={appearance.pieces === set.id}
                  onSelect={() => setAppearance({ pieces: set.id })}
                  name={set.name}
                  blurb={set.blurb}
                  meta={
                    <>
                      <Meter label="Readable" value={set.readability} />
                      <Meter label="Beautiful" value={set.beauty} />
                    </>
                  }
                >
                  <img
                    className="pieces-preview"
                    src={set.preview}
                    alt=""
                    width="240"
                    height="80"
                    loading="lazy"
                  />
                </OptionButton>
              ))}
            </div>
          </section>

          <section className="help-section appearance-section" aria-labelledby={boardsId}>
            <h3 id={boardsId}>Board</h3>
            <p className="appearance-hint">
              Boards are tuned so every piece set stays legible on them.
            </p>
            <div
              className="appearance-grid appearance-grid-boards"
              role="radiogroup"
              aria-labelledby={boardsId}
            >
              {BOARD_STYLES.map((board: BoardStyle) => (
                <OptionButton
                  key={board.id}
                  selected={appearance.board === board.id}
                  onSelect={() => setAppearance({ board: board.id })}
                  name={board.name}
                  blurb={board.blurb}
                >
                  <BoardPreview board={board} />
                </OptionButton>
              ))}
            </div>
          </section>

          <section className="help-section appearance-section" aria-labelledby={themesId}>
            <h3 id={themesId}>Site</h3>
            <p className="appearance-hint">The same pages and controls in a different voice.</p>
            <div
              className="appearance-grid appearance-grid-themes"
              role="radiogroup"
              aria-labelledby={themesId}
            >
              {SITE_THEMES.map((theme: SiteTheme) => (
                <OptionButton
                  key={theme.id}
                  selected={appearance.theme === theme.id}
                  onSelect={() => setAppearance({ theme: theme.id })}
                  name={theme.name}
                  blurb={theme.blurb}
                  meta={
                    <span className="theme-swatches" aria-hidden="true">
                      {theme.swatches.map((color) => (
                        <span key={color} style={{ background: color }} />
                      ))}
                    </span>
                  }
                >
                  <ThemePreview theme={theme} />
                </OptionButton>
              ))}
            </div>
          </section>
        </div>
        <footer className="help-footer appearance-footer">
          <button className="btn-ghost btn-sm" type="button" onClick={() => resetAppearance()}>
            Back to the originals
          </button>
          <button className="btn-primary btn-sm" type="button" onClick={onClose}>
            Done
          </button>
        </footer>
      </article>
    </ModalDialog>
  );
}
